import { listingsModel } from "models/listings";
import type { FeaturePreferences } from "models/types/agency-settings";
import type { APIListing } from "models/types/api-listing";
import type { LoaderFunctionArgs } from "react-router-dom";
import { VENDOR_PORTAL_PATHS } from "modules/vendor-portal/paths";
import { hasNonZeroInterestLevel } from "modules/vendor-portal/screens/feedback/utils";

export const listingsQuery = ({ params: { propertyId } }: LoaderFunctionArgs) =>
  listingsModel.queries.read(propertyId);

const getFeaturePreferences = (
  data: APIListing,
): FeaturePreferences | undefined => data.portal?.account?.preferences;

export const hasDocuments = (data: APIListing) =>
  data.documents && data.documents.length > 0;

export const hasOffers = (
  data: APIListing,
  featurePreferences?: FeaturePreferences,
) =>
  featurePreferences?.["vendor_portal.navigation.show_offers"] &&
  ((data.offers && data.offers.length > 0) ||
    (data.applicant_feedback?.entries || []).filter(
      (entry) => entry.type.id === "offer",
    ).length > 0);

export const hasMarketing = (data?: APIListing) => !!data?.marketing;

export const hasMarketingImages = (data?: APIListing) =>
  data?.marketing?.image || (data?.marketing?.images?.length ?? 0) > 0;

export const hasEvents = (data: APIListing) =>
  data.events &&
  data.events.some((event) =>
    ["private_inspection", "unaccompanied_inspection", "open_home"].includes(
      event.type.id,
    ),
  );

export const hasPerformance = (data: APIListing) =>
  data.performance && data.performance.stats.length > 0;

export const hasReachMarketing = (data: APIListing) =>
  data.performance?.stats.some((stat) => stat.format.id === "reach");

export const hasApplicantFeedback = (data: APIListing) =>
  data.applicant_feedback && data.applicant_feedback.entries.length > 0;

export const hasAgentUpdates = (data: APIListing) =>
  data.agent_updates && data.agent_updates.entries.length > 0;

export const hasPriceIndication = (
  data: APIListing,
  featurePreferences?: FeaturePreferences,
) =>
  featurePreferences?.[
    "vendor_portal.functionality.graphs.show_price_indicator_chart"
  ] &&
  data.summary?.list_of_price_indications?.value &&
  data.summary.list_of_price_indications.value.length > 0;

export const hasInterestLevelChart = (
  data: APIListing,
  featurePreferences?: FeaturePreferences,
) =>
  featurePreferences?.[
    "vendor_portal.functionality.interest_level.show_interest_level_data"
  ] &&
  hasNonZeroInterestLevel(
    data?.summary?.count_of_interested_groups_by_interest_level?.value,
  );

export const lockedRouteConfig = {
  [VENDOR_PORTAL_PATHS.DOCUMENTS]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasDocuments(data),
  },
  [VENDOR_PORTAL_PATHS.OFFERS]: {
    query: listingsQuery,
    getFeaturePreferences,
    isRouteLocked: (
      data: APIListing,
      featurePreferences?: FeaturePreferences,
    ) => !hasOffers(data, featurePreferences),
  },
  [VENDOR_PORTAL_PATHS.MARKETING]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasMarketing(data),
  },
  [VENDOR_PORTAL_PATHS.EVENTS]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasEvents(data),
  },
  [VENDOR_PORTAL_PATHS.EVENT]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasEvents(data),
  },
  [VENDOR_PORTAL_PATHS.PERFORMANCE]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasPerformance(data),
  },
  [VENDOR_PORTAL_PATHS.FEEDBACK]: {
    query: listingsQuery,
    getFeaturePreferences,
    isRouteLocked: (
      data: APIListing,
      featurePreferences?: FeaturePreferences,
    ) =>
      !hasApplicantFeedback(data) &&
      !hasInterestLevelChart(data, featurePreferences) &&
      !hasPriceIndication(data, featurePreferences),
  },
  [VENDOR_PORTAL_PATHS.AGENT_UPDATES]: {
    query: listingsQuery,
    isRouteLocked: (data: APIListing) => !hasAgentUpdates(data),
  },
};
